/** Scope-template editor: which permissions a template grants.
 *
 * Catalog-driven (PermissionScopeSections): permissions grouped by scope with
 * checkbox grant/revoke and per-scope + global bulk buttons — mirroring the
 * dashboard's AccessGroupEditPermissions, with three substitutions: access
 * tags → template ARGUMENTS, target access groups → templates of the same
 * group (plus the #guest-group placeholder), roles → the flags on General.
 *
 * Edits a local array; the parent dialog persists everything atomically via
 * the template's nested-write payload.
 */
import { Autocomplete, Box, Chip, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';

import type { AgroupTemplate, EmployeePermission, ScopeTemplateWriteInput } from '@/api/rbac';

import { PermissionScopeSections } from './PermissionScopeSections';

export const GUEST_GROUP_OPTION = '#guest-group' as const;

export interface ScopeRow extends ScopeTemplateWriteInput {
  permInfo: EmployeePermission;
}

interface Props {
  permCatalog: EmployeePermission[];
  /** Current argument aliases of the template being edited. */
  aliases: string[];
  /** Other templates of the same group + scope (target candidates). */
  groupTemplates: AgroupTemplate[];
  scopes: ScopeRow[];
  onChange: (scopes: ScopeRow[]) => void;
  readOnly?: boolean;
}

export function TemplatePermissionsEditor({
  permCatalog,
  aliases,
  groupTemplates,
  scopes,
  onChange,
  readOnly = false,
}: Props) {
  const granted = useMemo(() => new Set(scopes.map((s) => s.permInfo.codename)), [scopes]);
  const rowByCodename = useMemo(
    () => new Map(scopes.map((s) => [s.permInfo.codename, s])),
    [scopes],
  );
  const templateNameById = useMemo(
    () => new Map(groupTemplates.map((t) => [t.id, t.name])),
    [groupTemplates],
  );

  const mkRow = (perm: EmployeePermission): ScopeRow => ({
    perm: perm.codename,
    permInfo: perm,
    argument_aliases: [],
    all_tags: false,
    target_template_ids: [],
    target_guest_group: false,
  });

  function patchRow(codename: string, patch: Partial<ScopeRow>) {
    onChange(scopes.map((s) => (s.permInfo.codename === codename ? { ...s, ...patch } : s)));
  }

  function renderControls(perm: EmployeePermission) {
    const scope = rowByCodename.get(perm.codename);
    if (!scope) return null;
    const targetValue: (number | typeof GUEST_GROUP_OPTION)[] = [
      ...scope.target_template_ids,
      ...(scope.target_guest_group ? [GUEST_GROUP_OPTION] : []),
    ];
    return (
      <>
        {perm.has_target_tags && (
          <>
            <Tooltip title="Grant for ALL access tags (arguments ignored)">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch
                  size="small"
                  checked={scope.all_tags}
                  disabled={readOnly}
                  onChange={(e) => patchRow(perm.codename, { all_tags: e.target.checked })}
                />
                <Typography variant="caption">all tags</Typography>
              </Box>
            </Tooltip>
            <Autocomplete
              multiple
              size="small"
              sx={{ flex: 1, minWidth: 180 }}
              options={aliases}
              value={scope.argument_aliases.filter((a) => aliases.includes(a))}
              disabled={readOnly || scope.all_tags}
              onChange={(_, v) => patchRow(perm.codename, { argument_aliases: v })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Arguments" variant="standard" />}
            />
          </>
        )}
        {perm.has_target_agroups && (
          <Autocomplete<number | typeof GUEST_GROUP_OPTION, true>
            multiple
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            options={[...groupTemplates.map((t) => t.id), GUEST_GROUP_OPTION]}
            getOptionLabel={(opt) =>
              opt === GUEST_GROUP_OPTION ? GUEST_GROUP_OPTION : templateNameById.get(opt) ?? `#${opt}`
            }
            value={targetValue}
            disabled={readOnly}
            onChange={(_, v) =>
              patchRow(perm.codename, {
                target_template_ids: v.filter((x): x is number => typeof x === 'number'),
                target_guest_group: v.includes(GUEST_GROUP_OPTION),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target templates"
                variant="standard"
                helperText="Matched on shared argument aliases"
              />
            )}
          />
        )}
      </>
    );
  }

  return (
    <PermissionScopeSections
      catalog={permCatalog}
      granted={granted}
      readOnly={readOnly}
      onToggle={(perm, grant) =>
        onChange(
          grant
            ? [...scopes, mkRow(perm)]
            : scopes.filter((s) => s.permInfo.codename !== perm.codename),
        )
      }
      onBulk={(perms, grant) => {
        if (grant) {
          onChange([...scopes, ...perms.map(mkRow)]);
        } else {
          const drop = new Set(perms.map((p) => p.codename));
          onChange(scopes.filter((s) => !drop.has(s.permInfo.codename)));
        }
      }}
      allTagsOf={(perm) => Boolean(rowByCodename.get(perm.codename)?.all_tags)}
      onBulkAllTags={(perms, value) => {
        const affected = new Set(perms.map((p) => p.codename));
        onChange(
          scopes.map((s) =>
            affected.has(s.permInfo.codename) ? { ...s, all_tags: value } : s,
          ),
        );
      }}
      renderControls={renderControls}
    />
  );
}
